// Components/User/MiniCRM/DealChat.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";
import "../../../Assets/css/style.css";

const DealChat = () => {
  const navigate = useNavigate();
  const { id: dealId } = useParams();
  const currentUser = useSelector((s) => s.customer?.customer?.user);
  const bottomRef = useRef(null);
  const [dealerName, setDealerName] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 20;

  const fetchMessages = async (pg = 1, append = false) => {
    try {
      const res = await Handler({
        method: "GET",
        url: `/mini_crm/deal_msg_list.php?deal_id=${dealId}&page=${pg}&limit=${LIMIT}`,
      });

      if (res.success) {
        const msgs = res.data?.messages || [];

        setMessages((prev) => append ? [...msgs, ...prev] : msgs);

        const pagination = res.data?.pagination || {};
        setHasMore(pg < pagination.pages);

        // ✅ SET DEALER NAME
        if (res.data?.dealer_name) {
          setDealerName(res.data.dealer_name);
        }

      } else {
        toast.error(res.message || "Failed to load messages");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchMessages(1); }, [dealId]);

  // scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await Handler({
        method: "POST",
        url: "/mini_crm/msg.php",
        data: { deal_id: parseInt(dealId), message: text.trim() },
      });
      if (res.success) {
        setText("");
        fetchMessages(1); // refresh
      } else {
        toast.error(res.message || "Failed to send message");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      const res = await Handler({
        method: "POST",
        url: "/mini_crm/msg_delete.php",
        data: { message_id: messageId },
      });
      if (res.success) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        toast.success("Message deleted");
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch {
      toast.error("Something went wrong.");
    }
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchMessages(next, true);
  };

  const fmtTime = (dt) =>
    new Date(dt).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit",
    });

  const fmtDate = (dt) =>
    new Date(dt).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });

  // Group messages by date
  const grouped = messages.reduce((acc, msg) => {
    const key = fmtDate(msg.created_at);
    if (!acc[key]) acc[key] = [];
    acc[key].push(msg);
    return acc;
  }, {});

  return (
    <MainLayout>
      <FeedLayout>
        <div className="crm-page crm-chat-page">

          {/* Header */}
          <div className="crm-chat-header">
            <button
              className="crm-btn crm-btn--ghost"
              onClick={() => navigate("/user/crm")}
            >
              <i className="bx bx-arrow-back" />
            </button>
            <div className="crm-chat-header-info">
              <h2 className="crm-chat-title">
                {dealerName || "Loading..."}
              </h2>
            </div>
          </div>

          {/* Messages */}
          <div className="crm-chat-body">
            {loading ? (
              <div className="crm-loading">
                <div className="crm-spinner" />
              </div>
            ) : (
              <>
                {hasMore && (
                  <div className="crm-load-more">
                    <button className="crm-btn crm-btn--ghost" onClick={loadMore}>
                      Load earlier messages
                    </button>
                  </div>
                )}

                {Object.entries(grouped).map(([date, msgs]) => (
                  <div key={date}>
                    <div className="crm-chat-date-divider">
                      <span>{date}</span>
                    </div>
                    {msgs.map((msg) => {
                      const isMine = parseInt(msg.sender_id) === parseInt(currentUser?.id);
                      return (
                        <div
                          key={msg.id}
                          className={`crm-msg-row ${isMine ? "crm-msg-row--mine" : "crm-msg-row--theirs"}`}
                        >
                          {!isMine && (
                            <div className="crm-msg-avatar">
                              {msg.sender_name?.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="crm-msg-bubble-wrap">
                            {!isMine && (
                              <span className="crm-msg-sender">{msg.sender_name}</span>
                            )}
                            <div className={`crm-msg-bubble ${isMine ? "crm-msg-bubble--mine" : ""}`}>
                              <p className="crm-msg-text">{msg.message}</p>
                              <span className="crm-msg-time">{fmtTime(msg.created_at)}</span>
                            </div>
                            {isMine && (
                              <button
                                className="crm-msg-delete"
                                onClick={() => deleteMessage(msg.id)}
                                title="Delete message"
                              >
                                <i className="bx bx-trash" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div ref={bottomRef} />
              </>
            )}
          </div>

          {/* Input */}
          <form className="crm-chat-input-row" onSubmit={sendMessage}>
            <input
              className="crm-chat-input"
              placeholder="Type a message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={sending}
              autoComplete="off"
            />
            <button
              type="submit"
              className="crm-btn crm-btn--primary crm-chat-send"
              disabled={sending || !text.trim()}
            >
              {sending
                ? <span className="crm-btn-spinner" />
                : <i className="bx bx-send" />
              }
            </button>
          </form>

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default DealChat;